import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
  parsePersonalInfoContent,
  serializePersonalInfoContent,
  type PersonalInfoFields,
} from '../utils/personalInfoFields';
import RichTextToolbar from './RichTextToolbar';
import MarkdownSectionEditor from './MarkdownSectionEditor';

const PROFILE_TEXTAREA_ID = 'personal-info-profile-field';

/** Renders profile text with **bold** (and *italic*) so user sees how it will look in the main Preview. */
function ProfileFormattingPreview({ text }: { text: string }) {
  if (!text || !text.trim()) return null;
  const parts: React.ReactNode[] = [];
  let keyIdx = 0;
  const byBold = text.split(/\*\*/);
  for (let i = 0; i < byBold.length; i++) {
    if (i % 2 === 1) {
      parts.push(<strong key={keyIdx++} className="font-bold text-gray-900 dark:text-white">{byBold[i]}</strong>);
    } else {
      const seg = byBold[i] || '';
      const byItalic = seg.split(/\*([^*]+)\*/);
      for (let j = 0; j < byItalic.length; j++) {
        if (j % 2 === 1) parts.push(<em key={keyIdx++} className="italic">{byItalic[j]}</em>);
        else if (byItalic[j]) parts.push(<span key={keyIdx++}>{byItalic[j]}</span>);
      }
    }
  }
  return <span className="inline">{parts}</span>;
}

interface PersonalInfoFormProps {
  content: string;
  sectionTitle: string;
  onContentChange: (content: string) => void;
}

/**
 * Structured form for Personal Information / Contact section (Kickresume-style).
 * First name, Last name, Title, Phone, Email, Address (Street, City, Zip, State), Web, Profile.
 */
const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
  content,
  sectionTitle,
  onContentChange,
}) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState<PersonalInfoFields>(() => ({
    ...parsePersonalInfoContent(content),
    showPicture: true,
    customFields: parsePersonalInfoContent(content).customFields ?? [],
  }));

  useEffect(() => {
    const parsed = parsePersonalInfoContent(content);
    setFields((prev) => ({
      ...parsed,
      showPicture: prev?.showPicture ?? true,
      customFields: (prev?.customFields?.length ? prev.customFields : parsed.customFields) ?? [],
    }));
  }, [content]);

  const update = (updates: Partial<PersonalInfoFields>) => {
    const next = { ...fields, ...updates };
    setFields(next);
    onContentChange(serializePersonalInfoContent(next));
  };

  const addCustomField = () => {
    const custom = fields.customFields ?? [];
    update({ customFields: [...custom, { key: '', value: '' }] });
  };

  const updateCustomField = (index: number, key: string, value: string) => {
    const custom = [...(fields.customFields ?? [])];
    custom[index] = { key, value };
    update({ customFields: custom });
  };

  const removeCustomField = (index: number) => {
    const custom = (fields.customFields ?? []).filter((_, i) => i !== index);
    update({ customFields: custom });
  };

  return (
    <div className="flex flex-col min-h-0">
      <div className="py-2 space-y-3 flex-shrink-0">
        {/* Name row */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
              {t('optimizer.personalInfo.firstName', 'First name')}
            </label>
            <input
              type="text"
              value={fields.firstName}
              onChange={(e) => update({ firstName: e.target.value })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
              placeholder={t('optimizer.personalInfo.firstNamePlaceholder', 'First name')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
              {t('optimizer.personalInfo.lastName', 'Last name')}
            </label>
            <input
              type="text"
              value={fields.lastName}
              onChange={(e) => update({ lastName: e.target.value })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
              placeholder={t('optimizer.personalInfo.lastNamePlaceholder', 'Last name')}
            />
          </div>
        </div>

        {/* Title / Role */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
            {t('optimizer.personalInfo.title', 'Title / Role')}
          </label>
          <input
            type="text"
            value={fields.title}
            onChange={(e) => update({ title: e.target.value })}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
            placeholder={t('optimizer.personalInfo.titlePlaceholder', 'e.g. Sr Technical Manager')}
          />
        </div>

        {/* Phone & Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
              {t('optimizer.personalInfo.phone', 'Phone number')}
            </label>
            <input
              type="text"
              value={fields.phone}
              onChange={(e) => update({ phone: e.target.value })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
              placeholder="+1-234-567-8900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
              {t('optimizer.personalInfo.email', 'Email address')}
            </label>
            <input
              type="email"
              value={fields.email}
              onChange={(e) => update({ email: e.target.value })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
              placeholder="you@example.com"
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
            {t('optimizer.personalInfo.street', 'Street, number')}
          </label>
          <input
            type="text"
            value={fields.street}
            onChange={(e) => update({ street: e.target.value })}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
            placeholder={t('optimizer.personalInfo.streetPlaceholder', 'Street')}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
              {t('optimizer.personalInfo.city', 'City')}
            </label>
            <input
              type="text"
              value={fields.city}
              onChange={(e) => update({ city: e.target.value })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
              placeholder={t('optimizer.personalInfo.cityPlaceholder', 'City')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
              {t('optimizer.personalInfo.zipCode', 'Zip Code')}
            </label>
            <input
              type="text"
              value={fields.zipCode}
              onChange={(e) => update({ zipCode: e.target.value })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
              placeholder="Zip"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
              {t('optimizer.personalInfo.state', 'State')}
            </label>
            <input
              type="text"
              value={fields.state}
              onChange={(e) => update({ state: e.target.value })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
              placeholder={t('optimizer.personalInfo.statePlaceholder', 'State')}
            />
          </div>
        </div>

        {/* Web / LinkedIn */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
            {t('optimizer.personalInfo.web', 'Web / LinkedIn')}
          </label>
          <input
            type="url"
            value={fields.web}
            onChange={(e) => update({ web: e.target.value })}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] focus:border-transparent"
            placeholder="https://linkedin.com/in/..."
          />
        </div>

        {/* Add Custom Fields */}
        <div>
          <button
            type="button"
            onClick={addCustomField}
            className="flex items-center gap-1.5 text-sm font-medium text-[#6C47FF] dark:text-[#5B8DFF] hover:underline"
          >
            <Plus className="w-4 h-4" />
            {t('optimizer.personalInfo.addCustomFields', 'Add Custom Fields')}
          </button>
          {(fields.customFields ?? []).length > 0 && (
            <div className="mt-2 space-y-2">
              {(fields.customFields ?? []).map((cf, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={cf.key}
                    onChange={(e) => updateCustomField(idx, e.target.value, cf.value)}
                    placeholder={t('optimizer.personalInfo.customFieldLabel', 'Label')}
                    className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                  />
                  <input
                    type="text"
                    value={cf.value}
                    onChange={(e) => updateCustomField(idx, cf.key, e.target.value)}
                    placeholder={t('optimizer.personalInfo.customFieldValue', 'Value')}
                    className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
                  />
                  <button
                    type="button"
                    onClick={() => removeCustomField(idx)}
                    className="text-gray-400 hover:text-red-500 p-1"
                    aria-label={t('optimizer.remove', 'Remove')}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Profile (narrative) with formatting toolbar — select text then Bold/Italic etc.; preview shows formatted */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
            {t('optimizer.personalInfo.profile', 'Profile')}
          </label>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-1">
            {t('optimizer.formattingHint', 'Select text, then click Bold/Italic/Strikethrough/Underline. Formatting appears in both editor and preview.')}
          </p>
          <div className="rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
            <RichTextToolbar
              textareaId={PROFILE_TEXTAREA_ID}
              value={fields.profile}
              onChange={(v) => update({ profile: v })}
            />
            <MarkdownSectionEditor
              id={PROFILE_TEXTAREA_ID}
              value={fields.profile}
              onChange={(v) => update({ profile: v })}
              minHeight="72px"
              className="focus:ring-2 focus:ring-[#6C47FF] dark:focus:ring-[#5B8DFF] border-0"
              placeholder={t('optimizer.personalInfo.profilePlaceholder', 'Add information about yourself or what you are looking for in a new job...')}
            />
          </div>
          {fields.profile.trim() && (
            <div className="mt-1.5 px-2 py-1.5 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-700 dark:text-gray-300">
              <span className="text-gray-500 dark:text-gray-400 font-medium">{t('optimizer.previewFormatting', 'Preview')}: </span>
              <ProfileFormattingPreview text={fields.profile} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoForm;
